import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch, useSelector } from 'react-redux';

import 'react-datepicker/dist/react-datepicker.css';

import { crearPago } from '../../../../actions/pagosVentas';
import { listarMetodosPago } from '../../../../actions/configuracion';

const formatYYYYMMDD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const NuevoPagoModal = ({ show, onHide, onSuccess, idCliente }) => {
    const dispatch = useDispatch();

    const { metodos_pago } = useSelector(state => state.configuracion);

    const [fecha, setFecha] = useState(new Date());
    const [monto, setMonto] = useState('');
    const [metodoSel, setMetodoSel] = useState(null);
    const [referencia, setReferencia] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [moneda, setMoneda] = useState(0);
    const [tipoCambioUsd, setTipoCambioUsd] = useState('1');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!show) return;

        if (!metodos_pago?.length) {
            dispatch(listarMetodosPago());
        }
    }, [show, metodos_pago, dispatch]);

    useEffect(() => {
        if (show) return;

        setFecha(new Date());
        setMonto('');
        setMetodoSel(null);
        setReferencia('');
        setObservaciones('');
        setMoneda(0);
        setTipoCambioUsd('1');
        setGuardando(false);
    }, [show]);

    const opcionesMetodos = useMemo(() => {
        return (metodos_pago || []).map(m => ({ value: m._id, label: m.nombre }));
    }, [metodos_pago]);

    const getTc = () => {
        const tc = Number(tipoCambioUsd);
        return Number.isFinite(tc) && tc > 0 ? tc : 1;
    };

    const validarTipoCambioSiUSD = () => {
        if (Number(moneda) !== 1) return true;
        const tc = Number(tipoCambioUsd);
        return Number.isFinite(tc) && tc > 0;
    };

    const prefMoneda = Number(moneda) === 1 ? 'USD $' : '$';

    const puedeGuardar = useMemo(() => {
        return Boolean(Number(monto) > 0 && metodoSel?.value && validarTipoCambioSiUSD() && !guardando);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monto, metodoSel, moneda, tipoCambioUsd, guardando]);

    const onGuardar = async () => {
        if (!puedeGuardar || !idCliente) return;

        setGuardando(true);
        const ok = await dispatch(crearPago({
            id_cliente: idCliente,
            fecha: formatYYYYMMDD(fecha),
            monto: Number(monto),
            moneda: Number(moneda) === 1 ? 1 : 0,
            tipo_cambio: Number(moneda) === 1 ? getTc() : 1,
            metodo_pago: metodoSel.value,
            referencia: referencia.trim() || null,
            observaciones: observaciones.trim() || null
        }));
        setGuardando(false);

        if (!ok) return;

        if (typeof onSuccess === 'function') {
            onSuccess();
        } else if (typeof onHide === 'function') {
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Registrar pago</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="row">
                    <div className="col-6 mb-3">
                        <div className="text-muted small mb-1">Fecha</div>
                        <DatePicker
                            selected={fecha}
                            onChange={(d) => setFecha(d)}
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <div className="text-muted small mb-1">Monto</div>
                        <CurrencyInput
                            value={monto}
                            decimalsLimit={3}
                            allowNegativeValue={false}
                            placeholder={`${prefMoneda} Monto`}
                            className="form-control"
                            prefix={`${prefMoneda} `}
                            groupSeparator=","
                            decimalSeparator="."
                            onValueChange={(value) => setMonto(value || '')}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 mb-3">
                        <div className="text-muted small mb-1">Moneda</div>
                        <select
                            className="form-select"
                            value={moneda}
                            onChange={(e) => setMoneda(Number(e.target.value))}
                        >
                            <option value={0}>MXN</option>
                            <option value={1}>USD</option>
                        </select>
                    </div>
                    <div className="col-6 mb-3">
                        <div className="text-muted small mb-1">Tipo de cambio</div>
                        <input
                            type="number"
                            className="form-control"
                            min="0"
                            value={Number(moneda) === 1 ? tipoCambioUsd : '1'}
                            onChange={(e) => setTipoCambioUsd(e.target.value)}
                            disabled={Number(moneda) !== 1}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <Select
                        options={opcionesMetodos}
                        value={metodoSel}
                        onChange={setMetodoSel}
                        placeholder="Método de pago..."
                        noOptionsMessage={() => 'Sin resultados'}
                    />
                </div>

                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Referencia (opcional)"
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <textarea
                        className="form-control"
                        placeholder="Observaciones (opcional)"
                        rows={3}
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide} disabled={guardando}>
                    Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={onGuardar} disabled={!puedeGuardar}>
                    Guardar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default NuevoPagoModal;
